/**
 * ABK Upsell (snippets/abk-upsell.liquid)
 * Card che scelgono i valori di alcune opzioni prodotto (Cenone, Disco bus)
 * pilotando il variant picker nativo: le card sono solo un altro modo di
 * scegliere la variante, prezzo e SKU restano quelli di Shopify.
 * Concept ri-renderizza <variant-picker> a ogni cambio: la selezione
 * corrente si rilegge sempre dal picker, mai da uno stato locale, e dopo
 * ogni variantChange card e prezzi si risincronizzano.
 */
class AbkUpsell extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-abk-upsell-variants]')?.textContent || '[]');
    this.addEventListener('change', this.onChange);
    const pubsub = window.theme?.pubsub;
    this.unsubscribe = pubsub?.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.sectionId === this.dataset.sectionId) this.sync();
    });
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.unsubscribe?.();
  }

  get picker() {
    return document.getElementById(this.dataset.pickerId);
  }

  get items() {
    return Array.from(this.querySelectorAll('[data-abk-upsell-option]'));
  }

  // Valori selezionati nel picker, nell'ordine delle opzioni prodotto.
  selectedOptions() {
    const fieldsets = this.picker?.querySelectorAll('fieldset[data-option-index]') || [];
    return Array.from(fieldsets).map((fieldset) => fieldset.querySelector('input:checked')?.value);
  }

  optionValues(position) {
    const fieldset = this.picker?.querySelector(`fieldset[data-option-index="option${position}"]`);
    return Array.from(fieldset?.querySelectorAll('input[type="radio"]') || []).map((input) => input.value);
  }

  findVariant(options) {
    return this.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
  }

  // Valore "Sì" scelto per la card: la sotto-scelta se c'è, altrimenti il secondo valore.
  yesValue(item) {
    const choice = item.querySelector('[data-abk-upsell-choice]:checked');
    return choice ? choice.value : this.optionValues(item.dataset.position)[1];
  }

  onChange = (event) => {
    const item = event.target.closest('[data-abk-upsell-option]');
    if (!item) return;

    const toggle = item.querySelector('[data-abk-upsell-toggle]');
    if (event.target.matches('[data-abk-upsell-choice]') && !toggle.checked) return;

    const position = Number(item.dataset.position);
    const target = toggle.checked ? this.yesValue(item) : this.optionValues(position)[0];
    this.selectInPicker(position, target);
  };

  selectInPicker(position, value) {
    const fieldset = this.picker?.querySelector(`fieldset[data-option-index="option${position}"]`);
    const radio = Array.from(fieldset?.querySelectorAll('input[type="radio"]') || []).find((input) => input.value === value);
    if (!radio || radio.checked) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Stato delle card e prezzo "+X" ricalcolati dalle varianti sulla selezione corrente.
  sync() {
    const selected = this.selectedOptions();
    if (!selected.length) return;

    this.items.forEach((item) => {
      const position = Number(item.dataset.position);
      const index = position - 1;
      const values = this.optionValues(position);
      const toggle = item.querySelector('[data-abk-upsell-toggle]');
      const isOn = selected[index] !== values[0];

      toggle.checked = isOn;
      if (isOn) {
        const choice = Array.from(item.querySelectorAll('[data-abk-upsell-choice]')).find((input) => input.value === selected[index]);
        if (choice) choice.checked = true;
      }

      const base = this.findVariant(selected.map((value, i) => (i === index ? values[0] : value)));
      const upsell = this.findVariant(selected.map((value, i) => (i === index ? this.yesValue(item) : value)));
      const available = Boolean(base && upsell && upsell.available);

      item.classList.toggle('abk-upsell__item--unavailable', !available);
      toggle.disabled = !available && !isOn;

      const price = item.querySelector('[data-abk-upsell-price]');
      if (price) {
        price.textContent = available ? `+${this.formatMoney(upsell.price - base.price)}` : '';
      }
    });
  }

  formatMoney(cents) {
    const format = window.theme?.settings?.moneyFormat;
    const formatted = format ? window.theme.Currency.formatMoney(cents, format) : (cents / 100).toFixed(2);
    const text = new DOMParser().parseFromString(formatted, 'text/html').body.textContent || '';
    return text.replace(/[.,]00(?=\D*$)/, '');
  }
}

if (!customElements.get('abk-upsell')) {
  customElements.define('abk-upsell', AbkUpsell);
}
